import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { IHistoricoPedido } from '../../interface/IHistoricoPedido';
import { StatusPedido, StatusPedidosCores } from '../../enum/StatusPedido';
import { useDataHora } from '../../../core/hooks/useDataHora';

interface HistoricoPedidoTimelineProps {
    historico: IHistoricoPedido[];
    dataHoraEmissao: string;
}

interface EventoTimeline {
    dataHora: string;
    titulo: string;
    cor: string;
}

const COR_EMISSAO = '#78909c';

/** O status chega ora como número, ora como descrição, dependendo de quem montou o pedido. */
const descreverStatus = (status: IHistoricoPedido['status']): string =>
    typeof status === 'number' ? StatusPedido[status] : String(status);

/**
 * Histórico do pedido em linha do tempo vertical.
 *
 * Era um `Stepper` horizontal: com poucos status funcionava, mas pedidos que passam por
 * negociação, transporte e reenvio acumulam eventos, e aí os rótulos se sobrepunham e a
 * timeline só cabia com rolagem lateral. Na vertical cada evento ocupa uma linha própria e o
 * componente cresce com o conteúdo, sem limite prático de quantidade.
 */
const HistoricoPedidoTimeline = ({ historico, dataHoraEmissao }: HistoricoPedidoTimelineProps) => {
    const { dataHoraBr } = useDataHora();
    const cores: Record<string, string> = StatusPedidosCores;

    const eventos: EventoTimeline[] = [
        { dataHora: dataHoraEmissao, titulo: 'Pedido emitido', cor: COR_EMISSAO },
        ...historico.map(item => {
            const status = descreverStatus(item.status);
            return { dataHora: item.dataHora, titulo: status, cor: cores[status] ?? COR_EMISSAO };
        }),
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {eventos.map((evento, indice) => {
                const ultimo = indice === eventos.length - 1;

                return (
                    <Box key={`${evento.dataHora}-${indice}`} sx={{ display: 'flex', gap: 1.5, minHeight: ultimo ? 'auto' : 44 }}>
                        {/* Marcador e fio, numa coluna estreita à esquerda. */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                            <Box
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    marginTop: '4px',
                                    backgroundColor: evento.cor,
                                    // O halo destaca o evento atual sem precisar de outro elemento.
                                    boxShadow: theme => ultimo ? `0 0 0 4px ${alpha(evento.cor, 0.18)}` : 'none',
                                }}
                            />
                            {!ultimo && (
                                <Box
                                    sx={{
                                        flex: 1,
                                        width: 2,
                                        marginTop: '4px',
                                        borderRadius: 1,
                                        backgroundColor: theme => alpha(theme.palette.text.secondary, 0.15),
                                    }}
                                />
                            )}
                        </Box>

                        <Box sx={{ paddingBottom: ultimo ? 0 : 2, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: ultimo ? 700 : 500,
                                    lineHeight: 1.3,
                                    color: ultimo ? 'text.primary' : 'text.secondary',
                                }}
                            >
                                {evento.titulo}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {dataHoraBr(evento.dataHora)}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default HistoricoPedidoTimeline;
